import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@material-ui/core';
import { IList, tuple } from 'annotation-tools/utils';
import React, { FC, memo, MouseEvent, useMemo } from 'react';
import styled from 'styled-components';
import { colorDef } from '../../../constants/styles/color-def';
import { viewTexts } from '../../../constants/view-texts';
import { ICompanyType } from '../../../types/record/company';
import { BreadcrumbsJp } from '../../breadcrumbs/breadcrumbs';
import { CreateNewCompanyDialog } from '../../dialogs/create-new-company-dialog/create-new-company-dialog';
import { DeleteCompanyDialog } from '../../dialogs/delete-company-dialog/delete-company-dialog';
import { EditCompanyDialog } from '../../dialogs/edit-company-dialog/edit-company-dialog';
import { MuiButton } from '../../shared/form-element';
import { SearchBar } from '../../shared/form-element/mui-search-bar';
import { MuiPaper } from '../../shared/mui/mui-paper';
import { MuiTablePagination } from '../../shared/mui/mui-table-pagination';
import { MuiTableRow } from '../../shared/mui/mui-table-row';
import { H2 } from '../../shared/styled/heading';
import { Root } from '../../shared/styled/root';

const vt = viewTexts.pages.companyList;

const CreateButtonWrapper = styled.div`
  padding: 10px;
`;

const SearchBarWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const ButtonWrapper = styled.div`
  padding: 0 5px;
  display: inline-block;
`;

interface Props {
  searchString: string;
  onSearchStringChange: (value: string) => void;
  page: number;
  onPageChange: (value: number) => void;
  rowsPerPage: number;
  onRowsPerPageChange: (value: number) => void;
  numRows: number;
  companyListFiltered: IList<ICompanyType>;
  companyOnClick: (id: number) => void;
  createNewCompanyOnClick: () => void;
  createNewCompanyDialogOpen: boolean;
  createNewCompanyDialogOnClose: (reason: 'ok' | 'cancel' | '') => void;
  editCompanyOnClick: (company: ICompanyType) => void;
  editCompanyDialogIsOpen: boolean;
  editCompanyDialogOnClose: (reason: 'ok' | 'cancel' | '') => void;
  companyToEdit: ICompanyType | undefined;
  deleteCompanyOnClick: (company: ICompanyType) => void;
  deleteCompanyDialogIsOpen: boolean;
  deleteCompanyDialogOnClose: (reason: 'ok' | 'cancel' | '') => void;
  companyToDelete: ICompanyType | undefined;
}

export const CompanyListView: FC<Props> = memo(
  ({
    searchString,
    onSearchStringChange,
    page,
    onPageChange,
    rowsPerPage,
    onRowsPerPageChange,
    numRows,
    companyListFiltered,
    companyOnClick,
    createNewCompanyOnClick,
    createNewCompanyDialogOpen,
    createNewCompanyDialogOnClose,
    editCompanyOnClick,
    editCompanyDialogIsOpen,
    editCompanyDialogOnClose,
    companyToEdit,
    deleteCompanyOnClick,
    deleteCompanyDialogIsOpen,
    deleteCompanyDialogOnClose,
    companyToDelete
  }: Readonly<Props>) => {
    const companyListWithHandler = useMemo(
      () =>
        companyListFiltered.map(company =>
          tuple(
            {
              id: company.id,
              name: company.name,
              numProjects: company.numProjects,
              numModels: company.numModels,
              dateCreatedString: new Date(company.dateCreated).toLocaleString(),
              dateUpdatedString: new Date(company.dateUpdated).toLocaleString(),
              canBeDeleted: company.canBeDeleted
            },
            () => companyOnClick(company.id),
            (event: MouseEvent) => {
              event.stopPropagation();
              editCompanyOnClick(company);
            },
            (event: MouseEvent) => {
              event.stopPropagation();
              deleteCompanyOnClick(company);
            }
          )
        ),
      [
        companyListFiltered,
        companyOnClick,
        editCompanyOnClick,
        deleteCompanyOnClick
      ]
    );

    return (
      <Root>
        <BreadcrumbsJp />

        <H2>{vt.title}</H2>
        <SearchBarWrapper>
          <SearchBar
            placeholder={vt.searchBarPlaceholder}
            value={searchString}
            onInput={onSearchStringChange}
          />
          <CreateButtonWrapper>
            <MuiButton
              label={vt.buttons.create}
              onClick={createNewCompanyOnClick}
            />
          </CreateButtonWrapper>
        </SearchBarWrapper>

        <MuiPaper bgColor={colorDef.paperLighter} elevation={1}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{vt.header.name}</TableCell>
                <TableCell>{vt.header.numProjects}</TableCell>
                <TableCell>{vt.header.numModels}</TableCell>
                <TableCell>{vt.header.dateCreated}</TableCell>
                <TableCell>{vt.header.dateUpdated}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {companyListWithHandler.map(
                ([
                  company,
                  rowOnClickHandler,
                  editButtonOnClickHandler,
                  deleteButtonOnClickHandler
                ]) => (
                  <MuiTableRow key={company.id} onClick={rowOnClickHandler}>
                    <TableCell>{company.name}</TableCell>
                    <TableCell>{company.numProjects}</TableCell>
                    <TableCell>{company.numModels}</TableCell>
                    <TableCell>{company.dateCreatedString}</TableCell>
                    <TableCell>{company.dateUpdatedString}</TableCell>
                    <TableCell align='right'>
                      <ButtonWrapper>
                        <MuiButton
                          label={vt.buttons.edit}
                          onClick={editButtonOnClickHandler}
                        />
                      </ButtonWrapper>
                      <ButtonWrapper
                        title={
                          company.canBeDeleted
                            ? undefined
                            : viewTexts.undeletableReason.company
                        }
                      >
                        <MuiButton
                          color='secondary'
                          label={vt.buttons.delete}
                          onClick={deleteButtonOnClickHandler}
                          disabled={!company.canBeDeleted}
                        />
                      </ButtonWrapper>
                    </TableCell>
                  </MuiTableRow>
                )
              )}
            </TableBody>
          </Table>
          <MuiTablePagination
            numRows={numRows}
            page={page}
            onChangePage={onPageChange}
            rowsPerPage={rowsPerPage}
            onChangeRowsPerPage={onRowsPerPageChange}
          />
        </MuiPaper>
        <CreateNewCompanyDialog
          open={createNewCompanyDialogOpen}
          onClose={createNewCompanyDialogOnClose}
        />
        {companyToEdit !== undefined ? (
          <EditCompanyDialog
            open={editCompanyDialogIsOpen}
            onClose={editCompanyDialogOnClose}
            company={companyToEdit}
          />
        ) : (
          undefined
        )}
        {companyToDelete !== undefined ? (
          <DeleteCompanyDialog
            open={deleteCompanyDialogIsOpen}
            onClose={deleteCompanyDialogOnClose}
            company={companyToDelete}
          />
        ) : (
          undefined
        )}
      </Root>
    );
  }
);

CompanyListView.displayName = 'CompanyListView';
